import {
  createPublicClient,
  http,
  isAddress,
  parseAbi,
  parseEther,
  parseUnits,
  zeroAddress,
  type Address,
  type Hex,
} from 'viem';
import { base } from 'viem/chains';
import type { Charity, LaunchDraft, MarketToken } from '../types';
import { findCharity } from './charities';
import type { BaseWalletClient } from './wallet';
import { readDeployment } from './deployment';

export const CHAIN_ID = 8453;
export const DONATE_RELAY = '0x02A0d2a39732082b824a5A3D3b026C54d581DCC8' as const;
export const UNIVERSAL_ROUTER = '0x6ff5693b99212da76ad316178a184ab56d299b43' as const;
export const V4_QUOTER = '0x0d5e0F971ED27FBfF6c2837bf31316121532048D' as const;
export const PERMIT2 = '0x000000000022D473030F116dDEE9F6B43aC78BA3' as const;
export const WETH = '0x4200000000000000000000000000000000000006' as const;
export const ETH_USD_FEED = '0x71041dddad3595F9CEd3DcCFBe3D1F4b0a16Bb70' as const;

const rpcUrl = import.meta.env.VITE_BASE_RPC_URL || 'https://mainnet.base.org';
export const publicClient = createPublicClient({ chain: base, transport: http(rpcUrl) });

const deployment = readDeployment(import.meta.env);
export const KYNDO_ADDRESS = deployment.launchpad;
export const ENGINE_ADDRESS = deployment.factory;
export const KYNDO_HOOK = deployment.hook ?? zeroAddress;
export const isConfigured = deployment.configured;

type RegistryStack = { launchpad: Address; engine: Address; hook: Address };

function registryStacks(): RegistryStack[] {
  const stacks: RegistryStack[] = [];
  if (isConfigured && KYNDO_ADDRESS && ENGINE_ADDRESS) {
    stacks.push({ launchpad: KYNDO_ADDRESS, engine: ENGINE_ADDRESS, hook: KYNDO_HOOK });
  }
  return stacks;
}

export const launchpadAbi = parseAbi([
  'error LaunchesClosed()',
  'error ZeroConfigId()',
  'error UnapprovedCharity(bytes32 configId)',
  'error CharityShareTooSmall(uint16 asked,uint16 minimum)',
  'function count() view returns (uint256)',
  'function page(uint256 offset, uint256 limit) view returns ((address token,address curve,address distributor,address creator,bytes32 charityConfigId,uint16 charityBps,uint64 launchedAt)[])',
  'function entryOf(address token) view returns ((address token,address curve,address distributor,address creator,bytes32 charityConfigId,uint16 charityBps,uint64 launchedAt))',
  'function launchFee() view returns (uint256)',
  'function previewEconomics(uint256 launchConfigId) view returns (bytes32)',
  'function launch((string name,string symbol,string logo,string description,string website,string twitter,string farcaster,uint16 creatorTaxBps,bytes32 expectedEconomics,bytes32 salt,bytes32 charityConfigId,bytes32[] charityProof,address creatorPayout,uint16 charityBps) input,uint256 launchConfigId) payable returns (address token,address curve,address distributor)',
]);

const priceFeedAbi = parseAbi([
  'function decimals() view returns (uint8)',
  'function latestRoundData() view returns (uint80 roundId,int256 answer,uint256 startedAt,uint256 updatedAt,uint80 answeredInRound)',
]);

export async function fetchEthUsdPrice() {
  const [decimals, round] = await Promise.all([
    publicClient.readContract({ address: ETH_USD_FEED, abi: priceFeedAbi, functionName: 'decimals' }),
    publicClient.readContract({ address: ETH_USD_FEED, abi: priceFeedAbi, functionName: 'latestRoundData' }),
  ]);
  const answer = round[1];
  const updatedAt = round[3];
  const now = BigInt(Math.floor(Date.now() / 1000));
  if (answer <= 0n || updatedAt === 0n || updatedAt > now + 300n || now - updatedAt > 10_800n) {
    throw new Error('ETH/USD oracle price is unavailable or stale.');
  }
  return Number(answer) / 10 ** Number(decimals);
}

export const curveAbi = parseAbi([
  'function buy(uint256 quoteIn,uint256 minTokensOut,address recipient) payable returns (uint256 tokensOut)',
  'function sell(uint256 tokensIn,uint256 minQuoteOut,address recipient) returns (uint256 quoteOut)',
  'function getReserves() view returns (uint256 quoteReserve,uint256 tokenReserve)',
  'function realQuoteReserve() view returns (uint256)',
  'function graduationThreshold() view returns (uint256)',
  'function feeBps() view returns (uint256)',
  'function creatorTaxBps() view returns (uint256)',
  'function graduated() view returns (bool)',
]);

export const engineAbi = parseAbi([
  'function getLaunchedToken(address token) view returns ((address token,address curve,address deployer,address creatorFeeRecipient,address pairToken,uint256 graduationThreshold,uint24 poolFee,int24 tickSpacing,uint16 creatorTaxBps,bool buybackEnabled,uint8 phase,uint256 sweptQuote,uint256 sweptTokens,uint256 sweptAt,bool exists))',
]);

export const tokenAbi = parseAbi([
  'function name() view returns (string)',
  'function symbol() view returns (string)',
  'function logo() view returns (string)',
  'function description() view returns (string)',
  'function balanceOf(address owner) view returns (uint256)',
  'function allowance(address owner,address spender) view returns (uint256)',
  'function approve(address spender,uint256 amount) returns (bool)',
]);

export const distributorAbi = parseAbi([
  'function totalDonated() view returns (uint256)',
  'function totalToCreator() view returns (uint256)',
  'function pending() view returns (uint256)',
  'function harvest() returns (uint256)',
]);

type RegistryEntry = {
  token: Address;
  curve: Address;
  distributor: Address;
  creator: Address;
  charityConfigId: Hex;
  charityBps: number;
  launchedAt: bigint;
};

const v4QuoterAbi = [{
  type: 'function',
  name: 'quoteExactInputSingle',
  stateMutability: 'nonpayable',
  inputs: [{
    name: 'params',
    type: 'tuple',
    components: [
      {
        name: 'poolKey', type: 'tuple', components: [
          { name: 'currency0', type: 'address' },
          { name: 'currency1', type: 'address' },
          { name: 'fee', type: 'uint24' },
          { name: 'tickSpacing', type: 'int24' },
          { name: 'hooks', type: 'address' },
        ],
      },
      { name: 'zeroForOne', type: 'bool' },
      { name: 'exactAmount', type: 'uint128' },
      { name: 'hookData', type: 'bytes' },
    ],
  }],
  outputs: [{ name: 'amountOut', type: 'uint256' }, { name: 'gasEstimate', type: 'uint256' }],
}] as const;

function numeric(value: bigint, decimals = 18) {
  return Number(value) / 10 ** decimals;
}

function generatedFallbackLogo(symbol: string) {
  return `data:image/svg+xml,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect width="100" height="100" fill="#1155ee"/><text x="50" y="57" text-anchor="middle" font-family="Arial" font-size="25" font-weight="700" fill="#f3f1ea">${symbol.slice(0, 4)}</text></svg>`,
  )}`;
}

async function v4PriceEth(token: Address, hook: Address, poolFee: number, tickSpacing: number) {
  const amountIn = 100_000_000_000_000n;
  try {
    const simulation = await publicClient.simulateContract({
      address: V4_QUOTER,
      abi: v4QuoterAbi,
      functionName: 'quoteExactInputSingle',
      args: [{
        poolKey: { currency0: zeroAddress, currency1: token, fee: poolFee, tickSpacing, hooks: hook },
        zeroForOne: true,
        exactAmount: amountIn,
        hookData: '0x',
      }],
    });
    const [amountOut] = simulation.result;
    return amountOut === 0n ? 0 : Number(amountIn) / Number(amountOut);
  } catch {
    return 0;
  }
}

async function hydrateEntry(entry: RegistryEntry, charities: Charity[], stack: RegistryStack): Promise<MarketToken> {
  const [name, symbol, logo, description, launchState] = await Promise.all([
      publicClient.readContract({ address: entry.token, abi: tokenAbi, functionName: 'name' }),
      publicClient.readContract({ address: entry.token, abi: tokenAbi, functionName: 'symbol' }),
      publicClient.readContract({ address: entry.token, abi: tokenAbi, functionName: 'logo' }),
      publicClient.readContract({ address: entry.token, abi: tokenAbi, functionName: 'description' }),
      publicClient.readContract({
        address: stack.engine,
        abi: engineAbi,
        functionName: 'getLaunchedToken',
        args: [entry.token],
      }),
    ]);

  const cause = findCharity(charities, entry.charityConfigId);
  const enginePhase = Number(launchState.phase);
  const phase: MarketToken['phase'] = enginePhase === 2
    ? 'graduated'
    : enginePhase === 1
      ? 'finalizing'
      : enginePhase === 3
        ? 'rescued'
        : 'curve';
  const directLiquidity = phase === 'graduated' && entry.curve === zeroAddress;
  let progress = 100;
  let raisedEth = 0;
  let priceEth = 0;
  if (phase === 'curve' || phase === 'finalizing') {
    const [reserves, realQuote, threshold] = await Promise.all([
      publicClient.readContract({ address: entry.curve, abi: curveAbi, functionName: 'getReserves' }),
      publicClient.readContract({ address: entry.curve, abi: curveAbi, functionName: 'realQuoteReserve' }),
      publicClient.readContract({ address: entry.curve, abi: curveAbi, functionName: 'graduationThreshold' }),
    ]);
    progress = threshold === 0n ? 0 : Math.min(100, Number((realQuote * 10_000n) / threshold) / 100);
    raisedEth = numeric(realQuote);
    priceEth = reserves[1] === 0n ? 0 : Number(reserves[0]) / Number(reserves[1]);
  } else if (phase === 'graduated') {
    priceEth = await v4PriceEth(entry.token, stack.hook, Number(launchState.poolFee), Number(launchState.tickSpacing));
  }

  return {
    id: entry.token,
    address: entry.token,
    curve: entry.curve,
    distributor: entry.distributor,
    pairToken: launchState.pairToken,
    hook: stack.hook,
    poolFee: Number(launchState.poolFee),
    tickSpacing: Number(launchState.tickSpacing),
    directLiquidity,
    name,
    symbol,
    description,
    logo: logo || generatedFallbackLogo(symbol),
    charityName: cause?.n || 'Verified nonprofit',
    charityConfigId: entry.charityConfigId,
    charityLogo: cause?.l || '',
    charityBps: Number(entry.charityBps),
    creatorTaxBps: Number(launchState.creatorTaxBps),
    progress,
    raisedEth,
    volumeEth: raisedEth,
    priceEth,
    change24h: 0,
    launchedAt: Number(entry.launchedAt),
    phase,
  };
}

export async function fetchLiveLaunches(charities: Charity[], limit = 48): Promise<MarketToken[]> {
  if (!isConfigured) return [];
  const pages = await Promise.all(registryStacks().map(async (stack) => {
      const entries = (await publicClient.readContract({
        address: stack.launchpad,
        abi: launchpadAbi,
        functionName: 'page',
        args: [0n, BigInt(limit)],
      })) as readonly RegistryEntry[];
      return Promise.all(
        entries
          .map((entry) => hydrateEntry(entry, charities, stack)),
      );
  }));
  const seen = new Set<string>();
  return pages.flat().filter((token) => {
    const key = token.id.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  }).sort((a, b) => b.launchedAt - a.launchedAt);
}

export async function fetchLiveToken(address: Address, charities: Charity[]) {
  if (!isConfigured) return undefined;
  for (const stack of registryStacks()) {
    try {
      const entry = (await publicClient.readContract({
        address: stack.launchpad,
        abi: launchpadAbi,
        functionName: 'entryOf',
        args: [address],
      })) as RegistryEntry;
      return hydrateEntry(entry, charities, stack);
    } catch {
      // Only markets registered by the new platform are available here.
    }
  }
  return undefined;
}

export async function fetchDistributorTotals(address: Address) {
  const [donated, creator, pending] = await Promise.all([
    publicClient.readContract({ address, abi: distributorAbi, functionName: 'totalDonated' }),
    publicClient.readContract({ address, abi: distributorAbi, functionName: 'totalToCreator' }),
    publicClient.readContract({ address, abi: distributorAbi, functionName: 'pending' }),
  ]);
  return { donated, creator, pending };
}

export async function submitLaunch(
  draft: LaunchDraft,
  account: Address,
  getClient: () => Promise<BaseWalletClient>,
) {
  if (!isConfigured || !KYNDO_ADDRESS) throw new Error('New contracts are pending deployment.');
  const [launchFee, expectedEconomics] = await Promise.all([
    publicClient.readContract({ address: KYNDO_ADDRESS, abi: launchpadAbi, functionName: 'launchFee' }),
    publicClient.readContract({
      address: KYNDO_ADDRESS,
      abi: launchpadAbi,
      functionName: 'previewEconomics',
      args: [0n],
    }),
  ]);
  const entropy = crypto.getRandomValues(new Uint8Array(32));
  const salt = `0x${Array.from(entropy, (byte) => byte.toString(16).padStart(2, '0')).join('')}` as Hex;
  const input = {
    name: draft.name.trim(),
    symbol: draft.symbol.trim().toUpperCase(),
    logo: draft.logo.trim(),
    description: draft.description.trim(),
    website: draft.website.trim(),
    twitter: draft.twitter.trim(),
    farcaster: draft.farcaster.trim(),
    creatorTaxBps: draft.creatorTaxBps,
    expectedEconomics,
    salt,
    charityConfigId: draft.charityConfigId,
    charityProof: draft.charityProof,
    creatorPayout: account,
    charityBps: draft.charityBps,
  } as const;

  const simulation = await publicClient.simulateContract({
    account,
    address: KYNDO_ADDRESS,
    abi: launchpadAbi,
    functionName: 'launch',
    args: [input, 0n],
    value: launchFee,
  });
  const wallet = await getClient();
  const hash = await wallet.writeContract(simulation.request);
  return publicClient.waitForTransactionReceipt({ hash });
}

export async function quoteCurveBuy(curve: Address, eth: string) {
  const amount = parseEther(eth);
  const [reserves, feeBps, taxBps] = await Promise.all([
    publicClient.readContract({ address: curve, abi: curveAbi, functionName: 'getReserves' }),
    publicClient.readContract({ address: curve, abi: curveAbi, functionName: 'feeBps' }),
    publicClient.readContract({ address: curve, abi: curveAbi, functionName: 'creatorTaxBps' }),
  ]);
  const net = (amount * (10_000n - feeBps - taxBps)) / 10_000n;
  const output = (net * reserves[1]) / (reserves[0] + net);
  return { amount, output, minimum: (output * 9_900n) / 10_000n };
}

export async function buyOnCurve(
  curve: Address,
  eth: string,
  account: Address,
  getClient: () => Promise<BaseWalletClient>,
) {
  const quote = await quoteCurveBuy(curve, eth);
  const simulation = await publicClient.simulateContract({
    account,
    address: curve,
    abi: curveAbi,
    functionName: 'buy',
    args: [quote.amount, quote.minimum, account],
    value: quote.amount,
  });
  const wallet = await getClient();
  const hash = await wallet.writeContract(simulation.request);
  return publicClient.waitForTransactionReceipt({ hash });
}

export async function sellToCurve(
  token: Address,
  curve: Address,
  tokenAmount: string,
  account: Address,
  getClient: () => Promise<BaseWalletClient>,
) {
  const amount = parseUnits(tokenAmount, 18);
  const allowance = await publicClient.readContract({
    address: token,
    abi: tokenAbi,
    functionName: 'allowance',
    args: [account, curve],
  });
  const wallet = await getClient();
  if (allowance < amount) {
    const approval = await publicClient.simulateContract({
      account,
      address: token,
      abi: tokenAbi,
      functionName: 'approve',
      args: [curve, amount],
    });
    const approvalHash = await wallet.writeContract(approval.request);
    await publicClient.waitForTransactionReceipt({ hash: approvalHash });
  }

  const [reserves, feeBps, taxBps] = await Promise.all([
    publicClient.readContract({ address: curve, abi: curveAbi, functionName: 'getReserves' }),
    publicClient.readContract({ address: curve, abi: curveAbi, functionName: 'feeBps' }),
    publicClient.readContract({ address: curve, abi: curveAbi, functionName: 'creatorTaxBps' }),
  ]);
  const gross = (amount * reserves[0]) / (reserves[1] + amount);
  const output = (gross * (10_000n - feeBps - taxBps)) / 10_000n;
  const minimum = (output * 9_900n) / 10_000n;
  const sale = await publicClient.simulateContract({
    account,
    address: curve,
    abi: curveAbi,
    functionName: 'sell',
    args: [amount, minimum, account],
  });
  const hash = await wallet.writeContract(sale.request);
  return publicClient.waitForTransactionReceipt({ hash });
}
